/**
 * AI Configuration Management
 * Handles loading, validation, and persistence of AI service configuration
 */

import { AIConfig, AIError, AIErrorType } from './types';

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: Partial<AIConfig> = {
  defaultProvider: 'openai',
  temperature: 0.7,
  maxTokens: 2048,
  retryAttempts: 3,
  retryDelay: 1000,
  timeout: 30000,
};

/**
 * Configuration manager class
 */
export class ConfigManager {
  private config: AIConfig;
  private configKey = 'vibecode-ai-config';

  constructor() {
    this.config = this.loadConfig();
  }

  /**
   * Load configuration from environment and localStorage
   */
  private loadConfig(): AIConfig {
    // Start with defaults
    let config: AIConfig = {
      providers: {},
      defaultProvider: DEFAULT_CONFIG.defaultProvider!,
      ...DEFAULT_CONFIG,
    };

    // Load from environment variables
    if (typeof process !== 'undefined' && process.env) {
      config = this.loadFromEnvironment(config);
    }

    // Load from localStorage (client-side)
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem(this.configKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          config = this.mergeConfigs(config, parsed);
        } catch (error) {
          console.warn('Failed to parse stored AI config:', error);
        }
      }
    }

    return config;
  }

  /**
   * Load configuration from environment variables
   */
  private loadFromEnvironment(config: AIConfig): AIConfig {
    const env = process.env;

    // OpenAI configuration
    if (env.OPENAI_API_KEY) {
      config.providers.openai = {
        apiKey: env.OPENAI_API_KEY,
        baseUrl: env.OPENAI_BASE_URL,
        defaultModel: env.OPENAI_DEFAULT_MODEL || 'gpt-4o-mini',
        organization: env.OPENAI_ORGANIZATION,
      };
    }

    // Anthropic configuration
    if (env.ANTHROPIC_API_KEY) {
      config.providers.anthropic = {
        apiKey: env.ANTHROPIC_API_KEY,
        baseUrl: env.ANTHROPIC_BASE_URL,
        defaultModel: env.ANTHROPIC_DEFAULT_MODEL || 'claude-3-5-sonnet-20241022',
      };
    }

    // xAI configuration
    if (env.XAI_API_KEY) {
      config.providers.xai = {
        apiKey: env.XAI_API_KEY,
        baseUrl: env.XAI_BASE_URL,
        defaultModel: env.XAI_DEFAULT_MODEL || 'grok-beta',
      };
    }

    // Global settings
    if (env.AI_DEFAULT_PROVIDER) {
      config.defaultProvider = env.AI_DEFAULT_PROVIDER;
    }
    if (env.AI_DEFAULT_MODEL) {
      config.defaultModel = env.AI_DEFAULT_MODEL;
    }
    if (env.AI_TEMPERATURE) {
      config.temperature = parseFloat(env.AI_TEMPERATURE);
    }
    if (env.AI_MAX_TOKENS) {
      config.maxTokens = parseInt(env.AI_MAX_TOKENS, 10);
    }

    return config;
  }

  /**
   * Merge two configurations
   */
  private mergeConfigs(base: AIConfig, override: Partial<AIConfig>): AIConfig {
    return {
      ...base,
      ...override,
      providers: {
        ...base.providers,
        ...override.providers,
      },
    };
  }

  /**
   * Get current configuration
   */
  getConfig(): AIConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AIConfig>): void {
    this.config = this.mergeConfigs(this.config, updates);
    this.saveConfig();
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        // Don't save API keys to localStorage for security
        const configToSave = {
          ...this.config,
          providers: this.sanitizeProvidersForStorage(this.config.providers),
        };
        localStorage.setItem(this.configKey, JSON.stringify(configToSave));
      } catch (error) {
        console.error('Failed to save AI config:', error);
      }
    }
  }

  /**
   * Remove API keys before saving to localStorage
   */
  private sanitizeProvidersForStorage(providers: AIConfig['providers']): AIConfig['providers'] {
    const sanitized: AIConfig['providers'] = {};

    for (const [key, config] of Object.entries(providers)) {
      if (config) {
        sanitized[key as keyof AIConfig['providers']] = {
          ...config,
          apiKey: '', // Don't store API keys
        };
      }
    }

    return sanitized;
  }

  /**
   * Validate a provider configuration
   */
  validateProviderConfig(providerId: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const providerConfig = this.config.providers[providerId as keyof AIConfig['providers']];

    if (!providerConfig) {
      errors.push(`Provider ${providerId} is not configured`);
      return { valid: false, errors };
    }

    if (!providerConfig.apiKey || providerConfig.apiKey.trim() === '') {
      errors.push(`API key is missing for provider ${providerId}`);
    }

    // Provider-specific validation
    switch (providerId) {
      case 'openai':
        if (providerConfig.apiKey && !providerConfig.apiKey.startsWith('sk-')) {
          errors.push('OpenAI API key should start with "sk-"');
        }
        break;
      case 'anthropic':
        if (providerConfig.apiKey && !providerConfig.apiKey.includes('anthropic')) {
          errors.push('Invalid Anthropic API key format');
        }
        break;
      // Add more provider-specific validations as needed
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate entire configuration
   */
  validateConfig(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check if at least one provider is configured
    const configuredProviders = Object.entries(this.config.providers)
      .filter(([_, config]) => config && config.apiKey)
      .map(([id]) => id);

    if (configuredProviders.length === 0) {
      errors.push('No AI providers are configured');
    }

    // Validate default provider
    if (!configuredProviders.includes(this.config.defaultProvider)) {
      errors.push(`Default provider ${this.config.defaultProvider} is not configured`);
    }

    // Validate each configured provider
    for (const providerId of configuredProviders) {
      const validation = this.validateProviderConfig(providerId);
      errors.push(...validation.errors);
    }

    // Validate global settings
    if (this.config.temperature !== undefined && 
        (this.config.temperature < 0 || this.config.temperature > 2)) {
      errors.push('Temperature must be between 0 and 2');
    }

    if (this.config.maxTokens !== undefined && this.config.maxTokens < 1) {
      errors.push('Max tokens must be greater than 0');
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Get provider configuration
   */
  getProviderConfig(providerId: string): AIConfig['providers'][keyof AIConfig['providers']] {
    return this.config.providers[providerId as keyof AIConfig['providers']];
  }

  /**
   * Check if a provider is configured
   */
  isProviderConfigured(providerId: string): boolean {
    const config = this.getProviderConfig(providerId);
    return !!(config && config.apiKey);
  }

  /**
   * Reset configuration to defaults
   */
  reset(): void {
    this.config = {
      providers: {},
      defaultProvider: DEFAULT_CONFIG.defaultProvider!,
      ...DEFAULT_CONFIG,
    };
    this.saveConfig();
  }
}

// Export singleton instance
export const configManager = new ConfigManager();